import { createContext, useContext, useEffect, type ReactNode, type RefObject } from "react";

type DropdownContextValue = { close: () => void };
const DropdownContext = createContext<DropdownContextValue>({ close: () => {} });

interface DropdownMenuProps {
  /**
   * The wrapper element that contains BOTH the trigger and this menu.
   * Used to detect outside clicks (a click outside it closes the menu).
   */
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Which edge the menu aligns to. Left-aligned icons → "left"; right-aligned (e.g. a `⋮`) → "right". */
  align?: "left" | "right";
  /** Open below ("bottom", default) or above ("top") the trigger — use "top" near a screen edge. */
  side?: "top" | "bottom";
  testId?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Shared popover for icon-triggered menus. Renders below its trigger, anchored to
 * the left or right edge, with a real content width — and owns the close-on-outside-click
 * and close-on-Escape behaviour so callers don't re-implement it each time.
 *
 * Render it inside a `relative` wrapper next to the trigger, passing that wrapper's ref
 * as `anchorRef`:
 *
 *   <div className="relative" ref={ref}>
 *     <button onClick={() => setOpen(o => !o)}>…</button>
 *     {open && (
 *       <DropdownMenu anchorRef={ref} onClose={() => setOpen(false)}>
 *         <DropdownItem onSelect={doThing}>Label</DropdownItem>
 *       </DropdownMenu>
 *     )}
 *   </div>
 */
export function DropdownMenu({
  anchorRef,
  onClose,
  align = "left",
  side = "bottom",
  testId,
  className = "",
  children,
}: DropdownMenuProps) {
  useEffect(() => {
    // Close on "click", NOT "mousedown": a mousedown-based outside-close fires on a menu
    // item's own mousedown and unmounts it before its onClick runs, swallowing the
    // selection. Closing on "click" lets the item's onClick fire first.
    function onDocClick(e: MouseEvent) {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  return (
    <DropdownContext.Provider value={{ close: onClose }}>
      <div
        role="menu"
        data-testid={testId}
        className={`absolute z-20 w-max min-w-[11rem] max-w-[18rem] max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900 ${
          side === "top" ? "bottom-full mb-1" : "top-full mt-1"
        } ${align === "right" ? "right-0" : "left-0"} ${className}`}
      >
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

interface DropdownItemProps {
  onSelect: () => void;
  children: ReactNode;
  /** Renders the row in a destructive (red) tone. */
  danger?: boolean;
  disabled?: boolean;
  title?: string;
  className?: string;
}

/** A selectable row inside a DropdownMenu. Closes the menu, then runs `onSelect`. */
export function DropdownItem({ onSelect, children, danger = false, disabled = false, title, className = "" }: DropdownItemProps) {
  const { close } = useContext(DropdownContext);
  const tone = danger
    ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
    : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800";
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={title}
      onClick={() => {
        close();
        onSelect();
      }}
      className={`flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-40 ${tone} ${className}`}
    >
      {children}
    </button>
  );
}
